import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  Platform,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

const CANVAS_WIDTH = 1000;
const TOOLBAR_CANVAS_WIDTH = 700;
const TOOLBAR_CANVAS_HEIGHT = 112;
const DEFAULT_CANVAS_HEIGHT = 650;
const MIN_IMAGE_CANVAS_HEIGHT = 360;
const MAX_IMAGE_CANVAS_HEIGHT = 820;

const clamp01 = (value) => Math.max(0, Math.min(1, value));

const normalizeStoredValue = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed.startsWith("{")) return value;
    try {
      return JSON.parse(trimmed);
    } catch (_) {
      return value;
    }
  }

  return value;
};

const getSavedImage = (value) => {
  const normalized = normalizeStoredValue(value);
  if (!normalized || typeof normalized !== "object") return null;

  const fields = Array.isArray(normalized.fields) ? normalized.fields : [];
  const imageField = fields.find(
    (field) =>
      String(field?.field_type || field?.type || "").toLowerCase() === "image" &&
      typeof field?.field_values === "string" &&
      field.field_values.startsWith("data:image")
  );

  return imageField?.field_values || null;
};

const getStoredDrawingState = (value) => {
  const normalized = normalizeStoredValue(value);
  if (!normalized || typeof normalized !== "object") return null;
  return normalized.drawing_state || null;
};

const getLegacyText = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === "object") return null;
  } catch (_) {
    // Plain historical response from before drawing mode existed.
  }

  return trimmed;
};

export default function ExamDrawingAnswer({
  value,
  onChange = () => {},
  questionImageUri = null,
  readOnly = false,
  previewStatic = false,
  isDarkMode = false,
}) {
  const savedDrawingState = useMemo(() => getStoredDrawingState(value), [value]);
  const savedImage = useMemo(() => getSavedImage(value), [value]);
  const legacyText = useMemo(() => getLegacyText(value), [value]);

  const initialStrokes = Array.isArray(savedDrawingState?.strokes)
    ? savedDrawingState.strokes
    : [];

  const [tool, setTool] = useState("pen");
  const [surface, setSurface] = useState(
    savedDrawingState?.surface === "question_image" && questionImageUri
      ? "question_image"
      : "blank"
  );
  const [showGrid, setShowGrid] = useState(
    typeof savedDrawingState?.grid === "boolean"
      ? savedDrawingState.grid
      : true
  );
  const [strokes, setStrokes] = useState(initialStrokes);
  const [canvasHeight, setCanvasHeight] = useState(DEFAULT_CANVAS_HEIGHT);
  const [backgroundReady, setBackgroundReady] = useState(surface !== "question_image");

  const canvasRef = useRef(null);
  const toolbarCanvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const backgroundImageRef = useRef(null);
  const activeStrokeRef = useRef(null);
  const drawingRef = useRef(false);
  const strokesRef = useRef(initialStrokes);
  const pendingSurfacePersistRef = useRef(false);

  const colors = {
    panel: isDarkMode ? "#0f172a" : "#f8fafc",
    border: isDarkMode ? "#334155" : "#cbd5e1",
    text: isDarkMode ? "#e2e8f0" : "#0f172a",
    muted: isDarkMode ? "#94a3b8" : "#64748b",
    active: "#2563eb",
    button: isDarkMode ? "#1e293b" : "#ffffff",
  };

  const drawStroke = useCallback((ctx, stroke, width, height) => {
    if (!stroke || !Array.isArray(stroke.points) || stroke.points.length === 0) {
      return;
    }

    const points = stroke.points.map((point) => ({
      x: clamp01(Number(point.x) || 0) * width,
      y: clamp01(Number(point.y) || 0) * height,
    }));

    const normalizedWidth = Number(stroke.width) || (stroke.tool === "eraser" ? 0.035 : 0.005);
    const lineWidth = Math.max(2, normalizedWidth * Math.min(width, height));

    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = "#111827";
    ctx.fillStyle = "#111827";
    ctx.globalCompositeOperation =
      stroke.tool === "eraser" ? "destination-out" : "source-over";

    if (stroke.tool === "line") {
      if (points.length < 2) {
        ctx.restore();
        return;
      }
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y);
      ctx.stroke();
      ctx.restore();
      return;
    }

    if (points.length === 1) {
      ctx.beginPath();
      ctx.arc(points[0].x, points[0].y, lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }

    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i += 1) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.stroke();
    ctx.restore();
  }, []);

  const renderCanvas = useCallback(
    (
      strokesToDraw = strokesRef.current,
      activeStroke = activeStrokeRef.current,
      renderOptions = {}
    ) => {
      if (Platform.OS !== "web") return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const effectiveSurface = renderOptions.surface ?? surface;
      const effectiveGrid = renderOptions.grid ?? showGrid;

      const width = CANVAS_WIDTH;
      const height = canvasHeight;
      if (canvas.width !== width) canvas.width = width;
      if (canvas.height !== height) canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);

      if (effectiveSurface === "question_image" && backgroundImageRef.current) {
        const img = backgroundImageRef.current;
        const naturalWidth = img.naturalWidth || img.width || width;
        const naturalHeight = img.naturalHeight || img.height || height;
        const scale = Math.min(width / naturalWidth, height / naturalHeight);
        const drawWidth = naturalWidth * scale;
        const drawHeight = naturalHeight * scale;
        const drawX = (width - drawWidth) / 2;
        const drawY = (height - drawHeight) / 2;
        ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
      }

      if (effectiveGrid) {
        ctx.save();
        ctx.strokeStyle = "rgba(148, 163, 184, 0.35)";
        ctx.lineWidth = 1;
        const gridSize = 50;
        for (let x = gridSize; x < width; x += gridSize) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, height);
          ctx.stroke();
        }
        for (let y = gridSize; y < height; y += gridSize) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(width, y);
          ctx.stroke();
        }
        ctx.restore();
      }

      let overlay = overlayCanvasRef.current;
      if (!overlay) {
        overlay = document.createElement("canvas");
        overlayCanvasRef.current = overlay;
      }
      overlay.width = width;
      overlay.height = height;

      const overlayCtx = overlay.getContext("2d");
      overlayCtx.clearRect(0, 0, width, height);

      for (const stroke of strokesToDraw || []) {
        drawStroke(overlayCtx, stroke, width, height);
      }
      if (activeStroke) {
        drawStroke(overlayCtx, activeStroke, width, height);
      }

      ctx.drawImage(overlay, 0, 0);
    },
    [canvasHeight, drawStroke, showGrid, surface]
  );

  const persistDrawing = useCallback(
    (nextStrokes, nextSurface = surface, nextGrid = showGrid) => {
      strokesRef.current = nextStrokes;
      setStrokes(nextStrokes);

      if (!Array.isArray(nextStrokes) || nextStrokes.length === 0) {
        onChange("");
        if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(() => renderCanvas([], null));
        } else {
          renderCanvas([], null);
        }
        return;
      }

      if (Platform.OS !== "web") return;

      requestAnimationFrame(() => {
        renderCanvas(nextStrokes, null, { surface: nextSurface, grid: nextGrid });
        const canvas = canvasRef.current;
        if (!canvas) return;

        const image = canvas.toDataURL("image/png");
        onChange({
          answer_type: "drawing",
          version: 1,
          fields: [
            {
              field_type: "Image",
              field_values: image,
            },
          ],
          drawing_state: {
            surface: nextSurface,
            grid: nextGrid,
            strokes: nextStrokes,
          },
        });
      });
    },
    [onChange, renderCanvas, showGrid, surface]
  );

  useEffect(() => {
    strokesRef.current = strokes;
    renderCanvas(strokes, null);
  }, [renderCanvas, strokes]);

  useEffect(() => {
    if (Platform.OS !== "web") return;

    if (surface !== "question_image" || !questionImageUri) {
      backgroundImageRef.current = null;
      setBackgroundReady(true);
      setCanvasHeight(DEFAULT_CANVAS_HEIGHT);
      return;
    }

    setBackgroundReady(false);
    const image = new window.Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      backgroundImageRef.current = image;
      const naturalWidth = image.naturalWidth || image.width || CANVAS_WIDTH;
      const naturalHeight = image.naturalHeight || image.height || DEFAULT_CANVAS_HEIGHT;
      const targetHeight = Math.round(CANVAS_WIDTH * (naturalHeight / naturalWidth));
      setCanvasHeight(
        Math.max(MIN_IMAGE_CANVAS_HEIGHT, Math.min(MAX_IMAGE_CANVAS_HEIGHT, targetHeight))
      );
      setBackgroundReady(true);
    };
    image.onerror = () => {
      console.error("Exam drawing background image could not be loaded.");
      backgroundImageRef.current = null;
      setBackgroundReady(true);
      setSurface("blank");
      setCanvasHeight(DEFAULT_CANVAS_HEIGHT);
    };
    image.src = questionImageUri;
  }, [questionImageUri, surface]);

  useEffect(() => {
    if (!backgroundReady) return;
    if (surface === "question_image" && !backgroundImageRef.current) return;
    if (surface === "blank" && canvasHeight !== DEFAULT_CANVAS_HEIGHT) return;

    renderCanvas(strokesRef.current, null);

    if (pendingSurfacePersistRef.current && strokesRef.current.length > 0) {
      pendingSurfacePersistRef.current = false;
      persistDrawing(strokesRef.current, surface, showGrid);
    }
  }, [backgroundReady, canvasHeight, persistDrawing, renderCanvas, showGrid, surface]);

  const getPointFromEvent = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    return {
      x: clamp01((event.clientX - rect.left) / rect.width),
      y: clamp01((event.clientY - rect.top) / rect.height),
    };
  }, []);

  const beginStroke = useCallback(
    (event) => {
      if (readOnly || previewStatic || Platform.OS !== "web") return;
      if (surface === "question_image" && !backgroundReady) return;

      event.preventDefault?.();
      event.currentTarget?.setPointerCapture?.(event.pointerId);
      const point = getPointFromEvent(event);
      if (!point) return;

      drawingRef.current = true;
      activeStrokeRef.current = {
        tool,
        width: tool === "eraser" ? 0.04 : 0.006,
        points: [point],
      };
      renderCanvas(strokesRef.current, activeStrokeRef.current);
    },
    [backgroundReady, getPointFromEvent, previewStatic, readOnly, renderCanvas, surface, tool]
  );

  const moveStroke = useCallback(
    (event) => {
      if (!drawingRef.current || !activeStrokeRef.current) return;
      event.preventDefault?.();
      const point = getPointFromEvent(event);
      if (!point) return;

      const active = activeStrokeRef.current;
      if (active.tool === "line") {
        active.points = [active.points[0], point];
      } else {
        const last = active.points[active.points.length - 1];
        const dx = point.x - last.x;
        const dy = point.y - last.y;
        if (Math.sqrt(dx * dx + dy * dy) >= 0.0025) {
          active.points.push(point);
        }
      }
      renderCanvas(strokesRef.current, active);
    },
    [getPointFromEvent, renderCanvas]
  );

  const endStroke = useCallback(
    (event) => {
      if (!drawingRef.current || !activeStrokeRef.current) return;
      event?.preventDefault?.();
      drawingRef.current = false;

      const completed = activeStrokeRef.current;
      activeStrokeRef.current = null;

      if (completed.tool === "line" && completed.points.length >= 2) {
        const start = completed.points[0];
        const end = completed.points[completed.points.length - 1];
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        if (Math.sqrt(dx * dx + dy * dy) < 0.004) {
          renderCanvas(strokesRef.current, null);
          return;
        }
      }

      const next = [...strokesRef.current, completed];
      persistDrawing(next, surface, showGrid);
    },
    [persistDrawing, renderCanvas, showGrid, surface]
  );

  const undo = useCallback(() => {
    if (strokesRef.current.length === 0) return;
    const next = strokesRef.current.slice(0, -1);
    persistDrawing(next, surface, showGrid);
  }, [persistDrawing, showGrid, surface]);

  const clear = useCallback(() => {
    activeStrokeRef.current = null;
    drawingRef.current = false;
    persistDrawing([], surface, showGrid);
  }, [persistDrawing, showGrid, surface]);

  const toggleGrid = useCallback(() => {
    const nextGrid = !showGrid;
    setShowGrid(nextGrid);
    if (strokesRef.current.length > 0) {
      requestAnimationFrame(() => persistDrawing(strokesRef.current, surface, nextGrid));
    }
  }, [persistDrawing, showGrid, surface]);

  const toggleSurface = useCallback(() => {
    if (!questionImageUri) return;
    const nextSurface = surface === "question_image" ? "blank" : "question_image";

    setSurface(nextSurface);
    if (nextSurface === "question_image") {
      setShowGrid(false);
    }

    if (strokesRef.current.length > 0) {
      pendingSurfacePersistRef.current = true;
    }
  }, [questionImageUri, surface]);

  // BDD 08/30/2026 - Canvas-based toolbar for official exam drawing answers.
  // Physical pointer events on ordinary DOM/RNW buttons are swallowed by the
  // legacy test ScrollView/responder stack in this app. The drawing canvas itself
  // reliably receives pointer events, so the toolbar is intentionally a second
  // small canvas with explicit hit testing. This keeps the control path isolated
  // from the legacy responder system while preserving normal React state.
  const toolbarItems = useMemo(() => {
    const row1Y = 8;
    const row2Y = 62;
    const height = 42;
    const gap = 8;
    let x = 8;

    const row1 = [
      { id: "pen", label: "Pen", width: 86, kind: "tool" },
      { id: "line", label: "Straight Line", width: 142, kind: "tool" },
      { id: "eraser", label: "Eraser", width: 100, kind: "tool" },
      { id: "undo", label: "Undo", width: 88, kind: "action" },
      { id: "clear", label: "Clear", width: 88, kind: "action" },
      { id: "grid", label: "Grid", width: 88, kind: "toggle" },
    ].map((item) => {
      const positioned = { ...item, x, y: row1Y, height };
      x += item.width + gap;
      return positioned;
    });

    const row2 = questionImageUri
      ? [
          {
            id: "surface",
            label: surface === "question_image" ? "Use blank canvas" : "Use question image",
            width: 188,
            kind: "toggle",
            x: 8,
            y: row2Y,
            height,
          },
        ]
      : [];

    return [...row1, ...row2];
  }, [questionImageUri, surface]);

  const renderToolbarCanvas = useCallback(() => {
    if (Platform.OS !== "web") return;
    const canvas = toolbarCanvasRef.current;
    if (!canvas) return;

    if (canvas.width !== TOOLBAR_CANVAS_WIDTH) canvas.width = TOOLBAR_CANVAS_WIDTH;
    if (canvas.height !== TOOLBAR_CANVAS_HEIGHT) canvas.height = TOOLBAR_CANVAS_HEIGHT;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, TOOLBAR_CANVAS_WIDTH, TOOLBAR_CANVAS_HEIGHT);
    ctx.fillStyle = colors.panel;
    ctx.fillRect(0, 0, TOOLBAR_CANVAS_WIDTH, TOOLBAR_CANVAS_HEIGHT);

    const roundedRect = (x, y, width, height, radius) => {
      const r = Math.min(radius, width / 2, height / 2);
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + width - r, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + r);
      ctx.lineTo(x + width, y + height - r);
      ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
      ctx.lineTo(x + r, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    };

    toolbarItems.forEach((item) => {
      const active =
        item.kind === "tool"
          ? tool === item.id
          : item.id === "grid"
            ? showGrid
            : item.id === "surface"
              ? surface === "question_image"
              : false;

      roundedRect(item.x, item.y, item.width, item.height, 8);
      ctx.fillStyle = active ? "#eff6ff" : colors.button;
      ctx.fill();
      ctx.lineWidth = active ? 2 : 1;
      ctx.strokeStyle = active ? colors.active : colors.border;
      ctx.stroke();

      ctx.fillStyle = active ? colors.active : colors.text;
      ctx.font = `${active ? "700" : "500"} 15px Arial, sans-serif`;
      ctx.textBaseline = "middle";
      ctx.fillText(item.label, item.x + 12, item.y + item.height / 2);
    });
  }, [colors.active, colors.border, colors.button, colors.panel, colors.text, showGrid, surface, tool, toolbarItems]);

  useEffect(() => {
    if (Platform.OS !== "web" || readOnly || previewStatic) return;
    renderToolbarCanvas();
  }, [previewStatic, readOnly, renderToolbarCanvas]);

  const handleToolbarPointerDown = useCallback(
    (event) => {
      if (Platform.OS !== "web" || readOnly || previewStatic) return;
      const canvas = toolbarCanvasRef.current;
      if (!canvas) return;

      event.preventDefault?.();
      event.stopPropagation?.();

      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const x = ((event.clientX - rect.left) / rect.width) * TOOLBAR_CANVAS_WIDTH;
      const y = ((event.clientY - rect.top) / rect.height) * TOOLBAR_CANVAS_HEIGHT;

      const hit = toolbarItems.find(
        (item) =>
          x >= item.x &&
          x <= item.x + item.width &&
          y >= item.y &&
          y <= item.y + item.height
      );

      if (!hit) return;

      switch (hit.id) {
        case "pen":
          setTool("pen");
          break;
        case "line":
          setTool("line");
          break;
        case "eraser":
          setTool("eraser");
          break;
        case "undo":
          undo();
          break;
        case "clear":
          clear();
          break;
        case "grid":
          toggleGrid();
          break;
        case "surface":
          toggleSurface();
          break;
        default:
          break;
      }
    },
    [clear, previewStatic, readOnly, toggleGrid, toggleSurface, toolbarItems, undo]
  );


  if (readOnly) {
    return (
      <View
        style={{
          marginTop: 12,
          borderWidth: 1,
          borderColor: colors.border,
          borderRadius: 10,
          backgroundColor: colors.panel,
          padding: 12,
        }}
      >
        <Text style={{ fontWeight: "700", color: colors.text, marginBottom: 8 }}>
          Your drawing
        </Text>
        {savedImage ? (
          <Image
            source={{ uri: savedImage }}
            style={{ width: "100%", height: 360, resizeMode: "contain", backgroundColor: "#fff" }}
          />
        ) : legacyText ? (
          <View>
            <Text style={{ color: colors.muted, fontSize: 12, marginBottom: 4 }}>
              Previous text response (submitted before drawing mode was available)
            </Text>
            <Text style={{ color: colors.text }}>{legacyText}</Text>
          </View>
        ) : (
          <Text style={{ color: colors.muted }}>No drawing was submitted.</Text>
        )}
      </View>
    );
  }

  if (previewStatic) {
    return (
      <View
        style={{
          marginTop: 12,
          borderWidth: 1,
          borderStyle: "dashed",
          borderColor: colors.border,
          borderRadius: 10,
          backgroundColor: colors.panel,
          padding: 18,
          minHeight: 180,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MaterialCommunityIcons name="pencil-outline" size={30} color={colors.muted} />
        <Text style={{ marginTop: 8, fontWeight: "700", color: colors.text }}>
          Drawing / diagram answer
        </Text>
        <Text style={{ marginTop: 4, color: colors.muted, textAlign: "center" }}>
          Students will receive a drawing workspace for this question.
        </Text>
      </View>
    );
  }

  if (Platform.OS !== "web") {
    return (
      <View style={{ marginTop: 12, padding: 12, borderWidth: 1, borderColor: colors.border }}>
        <Text style={{ color: colors.text }}>
          Drawing answers are currently available in the web version of Saras-3D.
        </Text>
      </View>
    );
  }


  return (
    <View
      style={{
        marginTop: 12,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 12,
        backgroundColor: colors.panel,
        padding: 12,
      }}
    >
      <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        <View style={{ flex: 1, paddingRight: 8 }}>
          <Text style={{ fontWeight: "700", color: colors.text, fontSize: 14 }}>
            Drawing answer
          </Text>
          <Text style={{ color: colors.muted, fontSize: 12, marginTop: 2 }}>
            Draw with the mouse, pen, or touch. Use Straight Line for rays, graphs, geometry, and bonds.
          </Text>
        </View>
      </View>

      {React.createElement("canvas", {
        ref: toolbarCanvasRef,
        width: TOOLBAR_CANVAS_WIDTH,
        height: TOOLBAR_CANVAS_HEIGHT,
        onPointerDown: handleToolbarPointerDown,
        style: {
          display: "block",
          width: "100%",
          height: "auto",
          marginTop: "8px",
          marginBottom: "4px",
          backgroundColor: colors.panel,
          touchAction: "none",
          cursor: "pointer",
        },
        role: "toolbar",
        "aria-label": "Drawing tools",
      })}

      {surface === "question_image" && !backgroundReady ? (
        <Text style={{ color: colors.muted, fontSize: 12, marginBottom: 6 }}>
          Loading the question image into the drawing workspace…
        </Text>
      ) : null}

      {React.createElement("canvas", {
        ref: canvasRef,
        width: CANVAS_WIDTH,
        height: canvasHeight,
        onPointerDown: beginStroke,
        onPointerMove: moveStroke,
        onPointerUp: endStroke,
        onPointerCancel: endStroke,
        style: {
          display: "block",
          width: "100%",
          height: "auto",
          backgroundColor: "#ffffff",
          border: "1px solid #94a3b8",
          borderRadius: "8px",
          touchAction: "none",
          cursor: tool === "eraser" ? "cell" : "crosshair",
        },
        role: "img",
        "aria-label": "Drawing answer workspace",
      })}

      <Text style={{ color: colors.muted, fontSize: 11, marginTop: 6 }}>
        Your drawing is saved when you finish each stroke.
      </Text>
    </View>
  );
}
